import type { Logger } from "@expert/logging";

import ky, { HTTPError } from "ky";

import type { VerizonReplacementOption } from "./prepareVerizonReplacement";
import type { DefaultPayload } from "./types";

import { defaultHeaders, defaultTimeoutInMS } from "../types";

/** No ICCID means an eSIM. The line comes from `token`, the verified-line token, never from the body. */
type SubmitTypedVerizonDevicePayload = DefaultPayload & {
    token: string;
    imei: string;
    iccid?: string;
};

interface SubmitTypedVerizonDeviceResponse {
    eSim?: boolean;
    lineLast4?: string;
    imeiLast4?: string;
    token?: string;
}

/**
 * - `ACCEPTED`: `device` is the typed device as a replacement option, with the token that activates it.
 * - `REQUEST_FAILED`: anything else (403 when the expert isn't enabled, 400, 500, network).
 */
export type SubmitTypedVerizonDeviceResult =
    | { outcome: "ACCEPTED"; device: VerizonReplacementOption }
    | { outcome: "REQUEST_FAILED"; errorCode: string };

/** Whether the last digit of a 15-digit IMEI is its Luhn check digit, as the backend checks. */
export const hasValidImeiCheckDigit = (imei: string): boolean => {
    const sum = imei.split("").reduce((total, char, index) => {
        const digit = Number(char) * (index % 2 === 1 ? 2 : 1);
        return total + (digit > 9 ? digit - 9 : digit);
    }, 0);
    return sum % 10 === 0;
};

/**
 * Testing aid: gets an activation token for a device the expert typed in (its IMEI, and its ICCID for a physical
 * SIM) on the verified line, instead of a claim's device. Needs `isVerizonTypedImeiActivationEnabled`. The
 * response describes the device only by SIM type and the last 4 of the line and IMEI.
 */
export const submitTypedVerizonDevice = async (
    ssoAccessToken: string,
    payload: SubmitTypedVerizonDevicePayload,
    logger: Logger,
    apiBaseUrl: string,
): Promise<SubmitTypedVerizonDeviceResult> => {
    try {
        const url = `${apiBaseUrl}/api/device-activation/v1/verizon/typed-device`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: payload,
            timeout: defaultTimeoutInMS,
        });

        const { eSim, lineLast4, imeiLast4, token } = await resp.json<SubmitTypedVerizonDeviceResponse>();
        if (token && typeof eSim === "boolean" && lineLast4 && imeiLast4) {
            return {
                outcome: "ACCEPTED",
                device: { make: "", model: "", eSim, lineLast4, claimDate: "", typedImeiLast4: imeiLast4, token },
            };
        }

        logger.warn({ hasToken: !!token }, "Solve Guide | Error: Unexpected Verizon typed device response.");
        return { outcome: "REQUEST_FAILED", errorCode: "" };
    } catch (error) {
        // `ky` errors carry the request, whose body holds the IMEI and ICCID: log the status only.
        logger.warn(
            { httpStatus: error instanceof HTTPError ? error.response.status : undefined },
            "Solve Guide | Error: Could not submit the typed Verizon device.",
        );

        let errorCode = "";
        if (error instanceof HTTPError) {
            try {
                const errorJson = await error.response.json<{ code?: string }>();
                errorCode = errorJson.code ?? "";
            } catch {
                errorCode = "";
            }
        }

        return { outcome: "REQUEST_FAILED", errorCode };
    }
};
